import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { getCompanyBilling, startExtensionCheckout } from "../app/apis/subRatingsAPI";
import { IApiErrorBody, IErrorState } from "../app/interfaces/IApiResponses";
import { ICompanyBillingResponse } from "../app/interfaces/IBilling";
import { RootState } from "../app/store";
import { navigateWindow } from "../utils/System";

/*
* One company's own billing state: where the trial stands and what the next
* step costs them. Everything here is read from the API rather than assumed,
* because a company may have been quoted its own price.
*/
export interface IBillingSlice {
    status: 'idle' | 'loading' | 'failed';
    checkoutStatus: 'idle' | 'loading' | 'failed';
    data: ICompanyBillingResponse | null;
    error: undefined | IErrorState;
}

const initialState: IBillingSlice = {
    status: 'idle',
    checkoutStatus: 'idle',
    data: null,
    error: undefined,
};

export const getCompanyBillingThunk = createAsyncThunk(
    'billing/getCompanyBilling',
    async (companyId: number, { rejectWithValue }) => {
        try {
            return await getCompanyBilling(companyId);
        } catch (err) {
            const error = err as AxiosError<IApiErrorBody>;
            return rejectWithValue({ reason: error.response?.data['reason'], status: error.response?.status });
        }
    }
);

/*
* Hands the customer off to Stripe Checkout. Nothing is granted here - the
* trial is extended when Stripe confirms the payment, so the page they come
* back to re-reads the billing state rather than assuming it changed.
*/
export const startExtensionCheckoutThunk = createAsyncThunk(
    'billing/startExtensionCheckout',
    async (companyId: number, { rejectWithValue }) => {
        try {
            const response = await startExtensionCheckout(companyId);
            navigateWindow(response.url);
            return response;
        } catch (err) {
            const error = err as AxiosError<IApiErrorBody>;
            return rejectWithValue({ reason: error.response?.data['reason'], status: error.response?.status });
        }
    }
);

export const billingSlice = createSlice({
    name: 'billing',
    initialState,
    reducers: {
        clearBillingError: (state) => {
            state.error = undefined;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCompanyBillingThunk.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getCompanyBillingThunk.fulfilled, (state, action) => {
                state.status = 'idle';
                state.data = action.payload as ICompanyBillingResponse;
            })
            .addCase(getCompanyBillingThunk.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action?.payload as IErrorState;
            })
            .addCase(startExtensionCheckoutThunk.pending, (state) => {
                state.checkoutStatus = 'loading';
            })
            .addCase(startExtensionCheckoutThunk.fulfilled, (state) => {
                state.checkoutStatus = 'idle';
            })
            .addCase(startExtensionCheckoutThunk.rejected, (state, action) => {
                state.checkoutStatus = 'failed';
                state.error = action?.payload as IErrorState;
            });
    },
});

export const { clearBillingError } = billingSlice.actions;

export const selectBillingState = (state: RootState) => state.billing;
export const selectCompanyBilling = (state: RootState) => state.billing.data;

export default billingSlice.reducer;
